/**
 * AtlasPvpModule — the ground PvP wave's client (2026-09-18): the sessions
 * panel (duel invites, the ring, readiness, the countdown, the boundary
 * warning; the arena queue, staging, rounds and the relay; the conflict
 * sector's enrollment and lock), the chat's slash commands, the incoming
 * TELL of a committed blow, and the authoritative RESULT feedback — each
 * combat_result row confirms one cue, exactly once.
 *
 * Nothing here decides a fight: every button calls a reducer, every line is
 * read from the rows. `__pvp()` is the lever the probes and harnesses read.
 */
import type { App, IModule } from 'enginex/core/App.js';
import { Identity } from 'spacetimedb';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { sfx, sfxAt } from '../lib/sfx.js';
import { hitStop, cameraImpulse, KICK_DEALT_PITCH } from '../lib/combat_feel.js';
import {
  myDuel, pendingInvite, myMember, matchOf, membersOf, myQueue, myEnrollment, sectorOf, nearestSector, myActor, myRecord, tellAtMe,
  legalPlayerTargets, playerName, identityByName, myContext, usOf, nowMs, rows,
  type ResultRow, type ActionRow, type DuelRow,
} from '../lib/pvp_context.js';
import { ARENA_CAPTURE_S, FIELD_CAPTURE_S, DUEL_BOUNDARY_MS } from '../lib/combat_core.js';

const SUBS = [
  'SELECT * FROM pvp_duel', 'SELECT * FROM pvp_arena_match', 'SELECT * FROM pvp_arena_member', 'SELECT * FROM pvp_sector', 'SELECT * FROM pvp_enrollment',
  'SELECT * FROM pvp_arena_queue WHERE identity = :sender', 'SELECT * FROM combat_actor WHERE identity = :sender', 'SELECT * FROM pvp_record WHERE identity = :sender',
  'SELECT * FROM combat_action WHERE target_player = :sender', 'SELECT * FROM combat_action WHERE attacker = :sender',
  'SELECT * FROM combat_result WHERE attacker = :sender', 'SELECT * FROM combat_result WHERE target_player = :sender',
  'SELECT * FROM account',
];

const OUTCOME_LABEL: Record<string, string> = {
  hit: 'HIT', guarded: 'GUARDED', parried: 'PARRIED', dodged: 'DODGED', missed: 'MISSED', blocked: 'BLOCKED', downed: 'DOWNED', canceled: 'CANCELED', refused: 'REFUSED',
};
const OUTCOME_COLOR: Record<string, string> = {
  hit: '#ffd39a', guarded: '#bfe0ff', parried: '#fff1c2', dodged: '#c9d6e8', missed: '#9fb0c8', blocked: '#e59a7c', downed: '#ff9c8a', canceled: '#8a97a8', refused: '#8a97a8',
};
const fmtS = (ms: number): string => `${Math.max(0, ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
const mmss = (ms: number): string => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

export class AtlasPvpModule implements IModule {
  readonly name = 'GroundPvp';
  private panel: HTMLDivElement | null = null;
  private line: HTMLDivElement | null = null;
  private sub: HTMLDivElement | null = null;
  private buttons: HTMLDivElement | null = null;
  private feedback: HTMLDivElement | null = null;
  private tell: HTMLDivElement | null = null;
  private feedbackUntil = 0;
  private subscribedConn: unknown = null;
  private acc = 0;
  private lastHtml = '';
  private lastButtons = '';
  private seenResults = new Set<string>();
  private primedResults = false;
  private lastDuelState = '';
  private lastDuelId = 0n;
  private lastMatchState = '';
  private lastTellId = 0n;
  private lastFeedback: { text: string; color: string; at: number } | null = null;
  private log: Array<{ at: number; text: string }> = [];

  init(app: App): void {
    this.buildDom();
    app.world.addSystem(makeSystem('GroundPvp', Phase.UI, 32, () => this.frame()));
    bus.on('atlas:pvp-cmd', ({ cmd, args }) => { void this.command(cmd, args); });
    (window as unknown as Record<string, unknown>).__pvp = Object.assign(() => this.snapshot(), {
      cmd: (cmd: string, ...args: string[]) => this.command(cmd, args),
      legal: () => [...legalPlayerTargets()],
      log: () => this.log.slice(-20),
    });
  }

  dispose(): void {
    this.panel?.remove();
    delete (window as unknown as Record<string, unknown>).__pvp;
  }

  // ── DOM ──────────────────────────────────────────────────────────────────
  private buildDom(): void {
    const panel = document.createElement('div');
    panel.id = 'pvp-panel';
    panel.style.cssText = 'position:fixed;left:50%;top:76px;transform:translateX(-50%);z-index:39;display:none;flex-direction:column;align-items:center;gap:4px;'
      + 'font:12px system-ui;color:#dbe7f4;pointer-events:none;max-width:560px;text-align:center';
    const line = document.createElement('div');
    line.id = 'pvp-line';
    line.style.cssText = 'padding:4px 12px;border-radius:9px;background:rgba(10,16,26,0.82);border:1px solid #35506a;font-weight:700;letter-spacing:0.04em;text-shadow:0 1px 2px #000';
    const sub = document.createElement('div');
    sub.id = 'pvp-sub';
    sub.style.cssText = 'font:11px system-ui;color:#b9c8dd;text-shadow:0 1px 2px #000';
    const buttons = document.createElement('div');
    buttons.id = 'pvp-buttons';
    buttons.style.cssText = 'display:flex;gap:6px;pointer-events:auto';
    const feedback = document.createElement('div');
    feedback.id = 'pvp-feedback';
    feedback.style.cssText = 'display:none;font:700 13px system-ui;letter-spacing:0.12em;text-shadow:0 1px 3px #000';
    const tell = document.createElement('div');
    tell.id = 'pvp-tell';
    tell.style.cssText = 'display:none;padding:2px 10px;border-radius:8px;background:rgba(60,18,14,0.85);border:1px solid #c0533a;font:700 11px system-ui;color:#ffd0c0;letter-spacing:0.1em';
    panel.append(line, sub, buttons, tell, feedback);
    document.body.appendChild(panel);
    this.panel = panel; this.line = line; this.sub = sub; this.buttons = buttons; this.feedback = feedback; this.tell = tell;
  }

  private button(label: string, cmd: string, tone = '#2a3a52'): string {
    return `<button data-pvp="${cmd}" style="padding:3px 10px;border-radius:7px;border:1px solid #4a6a8f;background:${tone};color:#eaf2fc;font:600 11px system-ui;cursor:pointer">${label}</button>`;
  }

  // ── Frame ────────────────────────────────────────────────────────────────
  private frame(): void {
    const conn = atlas.conn;
    if (!conn) return;
    if (this.subscribedConn !== conn) {
      this.subscribedConn = conn;
      // Primed only once the subscription has APPLIED: result rows that arrive as history never replay as cues.
      try { conn.subscriptionBuilder().onApplied(() => { this.primedResults = true; }).subscribe(SUBS); } catch { /* pre-publish bindings */ }
      this.hookTables(conn);
    }
    const nowP = performance.now();
    if (this.feedback && this.feedbackUntil && nowP > this.feedbackUntil) { this.feedbackUntil = 0; this.feedback.style.display = 'none'; }
    if (++this.acc % 6 !== 0) return; // ~10 Hz
    this.render();
  }

  private hookTables(conn: NonNullable<typeof atlas.conn>): void {
    const db = conn.db as unknown as {
      combatResult?: { onInsert(cb: (c: unknown, row: ResultRow) => void): void };
      combatAction?: { onInsert(cb: (c: unknown, row: ActionRow) => void): void; onDelete(cb: (c: unknown, row: ActionRow) => void): void };
    };
    db.combatResult?.onInsert((_c, row) => this.onResult(row));
    db.combatAction?.onInsert((_c, row) => {
      if (row.targetKind !== 'player' || atlas.identityHex === null || row.targetPlayer.toHexString() !== atlas.identityHex) return;
      // The tell has a sound before it has a shape — and it comes FROM the opponent: which way to turn is the whole
      // value of a telegraph. An attacker outside the AOI mirror keeps the head-locked cue rather than a guess.
      const foe = atlas.remotes.get(row.attacker.toHexString());
      if (foe) sfxAt('sfx_creature_attack', { x: foe.targetX, y: atlas.localY + 1.2, z: foe.targetZ }, { gain: 0.3, cooldownMs: 200, priority: 88, caption: 'Attack incoming' });
      else sfx('sfx_creature_attack', { gain: 0.3, cooldownMs: 200 });
      bus.emit('atlas:pvp-tell', { attackerHex: row.attacker.toHexString(), itemKey: row.itemKey, impactInMs: usOf(row.impactAt) - nowMs() });
    });
    db.combatAction?.onDelete((_c, row) => {
      if (row.targetKind !== 'player' || atlas.identityHex === null || row.targetPlayer.toHexString() !== atlas.identityHex) return;
      if (!tellAtMe()) bus.emit('atlas:pvp-tell', null);
    });
  }

  /** ONE cue per result row. The first sight of the subscription is history — primed, never replayed. */
  private onResult(row: ResultRow): void {
    const key = row.resultId.toString();
    if (this.seenResults.has(key)) return;
    this.seenResults.add(key);
    if (this.seenResults.size > 400) { const first = this.seenResults.values().next().value; if (first !== undefined) this.seenResults.delete(first); }
    if (!this.primedResults) return;
    const me = atlas.identityHex;
    if (me === null) return;
    const mine = row.attacker.toHexString() === me;
    const atMe = row.targetKind === 'player' && row.targetPlayer.toHexString() === me;
    if (!mine && !atMe) return;
    // Only PLAYER-versus-player results carry a cue here (creature / townsperson hits keep the wilds' own feel).
    if (row.targetKind !== 'player') { if (row.outcome === 'blocked' && mine) this.showFeedback('BLOCKED', OUTCOME_COLOR.blocked); return; }
    const label = OUTCOME_LABEL[row.outcome] ?? row.outcome.toUpperCase();
    if (mine) {
      this.showFeedback(row.dmg > 0 ? `${label} ${row.dmg}` : label, OUTCOME_COLOR[row.outcome] ?? '#dbe7f4');
      if (row.outcome === 'hit' || row.outcome === 'guarded' || row.outcome === 'downed') { cameraImpulse(-KICK_DEALT_PITCH * 0.5, 0); sfx('sfx_impact_energy', { gain: 0.25, cooldownMs: 120 }); }
      else if (row.outcome === 'parried') sfx('sfx_impact_energy', { gain: 0.5, cooldownMs: 120 });
      else if (row.outcome === 'blocked') sfx('sfx_impact_slug', { gain: 0.35, cooldownMs: 120 });
    } else if (atMe) {
      const who = playerName(row.attacker.toHexString());
      if (row.outcome === 'hit' || row.outcome === 'guarded' || row.outcome === 'downed') {
        this.showFeedback(`${who}: ${row.dmg}${row.outcome === 'guarded' ? ' (guarded)' : ''}${row.outcome === 'downed' ? ' — DOWN' : ''}`, '#ffb3a3');
        hitStop(performance.now(), row.outcome === 'downed'); // a competitive dose: short, never freezing the sim
      } else if (row.outcome === 'parried') this.showFeedback(`You parried ${who}`, OUTCOME_COLOR.parried);
      else if (row.outcome === 'dodged') this.showFeedback(`You dodged ${who}`, OUTCOME_COLOR.dodged);
    }
    this.log.push({ at: nowMs(), text: `${mine ? 'out' : 'in'} ${row.outcome} ${row.dmg} ${row.itemKey || 'hands'} ${row.contextKind}` });
    if (this.log.length > 60) this.log.shift();
  }

  private showFeedback(text: string, color: string): void {
    if (!this.feedback) return;
    this.feedback.textContent = text;
    this.feedback.style.color = color;
    this.feedback.style.display = 'block';
    this.feedbackUntil = performance.now() + 1500;
    this.lastFeedback = { text, color, at: nowMs() };
  }

  // ── Render ───────────────────────────────────────────────────────────────
  private render(): void {
    if (!this.panel || !this.line || !this.sub || !this.buttons || !this.tell) return;

    const onGround = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight && !atlas.loadingGate;
    let html = '', sub = '', buttons = '';
    const now = nowMs();
    const me = atlas.identityHex;
    if (onGround && me !== null) {
      const invite = pendingInvite();
      const duel = myDuel();
      const mem = myMember();
      const match = mem ? matchOf(mem.matchId) : null;
      const queue = myQueue();
      const enroll = myEnrollment();
      if (invite) {
        // The invitee's own live duel IS the invitation: the prompt comes first, or it would never render.
        html = `⚔ ${playerName(invite.a.toHexString())} challenges you to a duel`;
        sub = `standard weapons, 100 hp, 90 s, the sparring ring by the arrival pad · expires in ${fmtS(usOf(invite.invitedAt) + 30_000 - now)}`;
        buttons = this.button('Accept', 'accept', '#244a2c') + this.button('Decline', 'decline');
      } else if (duel && duel.state !== 'finished') {
        const other = duel.a.toHexString() === me ? duel.b : duel.a;
        const name = playerName(other.toHexString());
        const dRing = Math.hypot(atlas.localX - duel.ringX, atlas.localZ - duel.ringZ);
        const iAmA = duel.a.toHexString() === me;
        const myReady = iAmA ? duel.readyA : duel.readyB, theirReady = iAmA ? duel.readyB : duel.readyA;
        if (duel.state === 'invited') {
          if (iAmA) { html = `⚔ Challenge sent to ${name}`; sub = 'waiting for their answer'; buttons = this.button('Withdraw', 'cancel'); }
        } else if (duel.state === 'staging') {
          html = `⚔ DUEL vs ${name} — ${dRing <= duel.ringR ? 'in the ring' : `walk to the ring (${dRing.toFixed(0)} m)`}`;
          sub = `${myReady ? 'you are ready' : 'ready up inside the ring'} · ${theirReady ? `${name} is ready` : `${name} not ready`} · competitive rules: standard weapons, 100 hp, feeds full`;
          buttons = (myReady ? '' : this.button('Ready', 'ready', '#244a2c')) + this.button('Cancel', 'cancel');
        } else if (duel.state === 'countdown') {
          html = `⚔ DUEL vs ${name} begins in ${fmtS(usOf(duel.startsAt) - now)}`;
          buttons = this.button('Cancel', 'cancel');
        } else if (duel.state === 'active') {
          const out = iAmA ? duel.outSinceA : duel.outSinceB;
          const outMs = out.microsSinceUnixEpoch === 0n ? 0 : now - usOf(out);
          html = `⚔ DUEL vs ${name} · ${mmss(usOf(duel.endsAt) - now)} left`;
          sub = outMs > 0 ? `RETURN TO THE RING — ${fmtS(DUEL_BOUNDARY_MS - outMs)}` : `ring ${duel.ringR.toFixed(0)} m · ${dRing.toFixed(0)} m from centre`;
          buttons = this.button('Yield', 'yield', '#4a2a2a');
        }
      } else if (mem && match && match.state !== 'finished') {
        const members = membersOf(match.matchId);
        const aliveA = members.filter((m) => m.team === 'A' && m.alive).length, aliveB = members.filter((m) => m.team === 'B' && m.alive).length;
        const sx = mem.team === 'A' ? match.stagingAx : match.stagingBx, sz = mem.team === 'A' ? match.stagingAz : match.stagingBz;
        const dStage = Math.hypot(atlas.localX - sx, atlas.localZ - sz);
        const score = `A ${match.winsA} – ${match.winsB} B${match.draws ? ` (${match.draws} drawn)` : ''}`;
        if (match.state === 'staging' || match.state === 'between') {
          html = `RELAY SKIRMISH ${match.teamSize}v${match.teamSize} · team ${mem.team} · ${match.state === 'between' ? `round ${match.round} over · ${score}` : 'match found'}`;
          sub = `${mem.ready ? 'ready' : dStage <= 8 ? 'at your staging pad — ready up' : `go to staging ${mem.team} (${dStage.toFixed(0)} m)`} · ${members.filter((m) => m.ready).length}/${members.length} ready · ${fmtS(usOf(match.readyDeadline) - now)} to ready`;
          buttons = (mem.ready ? '' : this.button('Ready', 'ready', '#244a2c')) + this.button('Leave', 'leave');
        } else if (match.state === 'countdown') {
          html = `ROUND ${match.round + 1} in ${fmtS(usOf(match.roundStartedAt) - now)} · ${score}`;
        } else if (match.state === 'round') {
          const relayIn = usOf(match.relayActiveAt) - now;
          const relay = relayIn > 0 ? `relay wakes in ${fmtS(relayIn)}` : `relay A ${match.capA.toFixed(1)} / B ${match.capB.toFixed(1)} of ${ARENA_CAPTURE_S} s`;
          html = `ROUND ${match.round} · ${score} · ${mmss(usOf(match.roundEndsAt) - now)}`;
          sub = `${mem.alive ? 'you are in' : 'you are out this round'} · alive A ${aliveA} / B ${aliveB} · ${relay}`;
          buttons = this.button('Leave', 'leave');
        }
      } else if (queue) {
        html = `Queued: Relay Skirmish ${queue.teamSize}v${queue.teamSize}`;
        sub = 'a match forms when enough travelers queue at this settlement';
        buttons = this.button('Leave queue', 'leavequeue');
      } else if (enroll) {
        const s = sectorOf(enroll.sectorId);
        const lockLeft = usOf(enroll.lockUntil) - now;
        const state = enroll.state === 'enrolling' ? `enrolling ${fmtS(usOf(enroll.stateAt) + 5000 - now)}`
          : enroll.state === 'engaged' ? `ENGAGED · lock ${fmtS(lockLeft)}`
            : enroll.state === 'leaving' ? `leaving ${fmtS(usOf(enroll.leaveAt) - now)}` : 'ELIGIBLE';
        html = `CONFLICT · ${s?.name ?? 'sector'} · side ${enroll.side} · ${state}`;
        sub = s ? `relay ${s.sideA} ${s.capA.toFixed(1)} / ${s.sideB} ${s.capB.toFixed(1)} of ${FIELD_CAPTURE_S} s · score ${s.scoreA}–${s.scoreB}` : '';
        buttons = this.button(enroll.state === 'engaged' ? 'Surrender' : 'Disengage', 'disengage', enroll.state === 'engaged' ? '#4a2a2a' : '#2a3a52');
      } else {
        // Finished sessions linger for a result line.
        const fin = duel && duel.state === 'finished' && now - usOf(duel.finishedAt) < 8000 ? duel : null;
        if (fin) { html = this.duelResultLine(fin, me); }
      }
      const t = tellAtMe();
      if (t) {
        const left = usOf(t.impactAt) - now;
        this.tell.textContent = `⚠ ${playerName(t.attacker.toHexString())} — ${t.itemKey.replace(/_/g, ' ') || 'a blow'} · ${fmtS(left)}`;
        if (this.tell.style.display !== 'block') this.tell.style.display = 'block';
      } else if (this.tell.style.display !== 'none') this.tell.style.display = 'none';
      this.watchTransitions(duel, match?.state ?? '');
    }
    const show = html !== '' || this.feedbackUntil > 0 || this.tell.style.display === 'block';
    if (this.panel.style.display !== (show ? 'flex' : 'none')) this.panel.style.display = show ? 'flex' : 'none';
    if (html !== this.lastHtml) {
      this.lastHtml = html;
      this.line.textContent = html;
      this.line.style.display = html ? 'block' : 'none';
    }
    if (this.sub.textContent !== sub) { this.sub.textContent = sub; this.sub.style.display = sub ? 'block' : 'none'; }
    if (buttons !== this.lastButtons) {
      this.lastButtons = buttons;
      this.buttons.innerHTML = buttons;
      this.buttons.style.display = buttons ? 'flex' : 'none';
      for (const b of this.buttons.querySelectorAll<HTMLButtonElement>('button[data-pvp]')) b.onclick = () => { void this.command(b.dataset.pvp ?? '', []); };
    }
  }

  private duelResultLine(d: DuelRow, me: string): string {
    const other = playerName((d.a.toHexString() === me ? d.b : d.a).toHexString());
    const winnerHex = d.winner.toHexString();
    const won = winnerHex === me, lost = !won && winnerHex !== Identity.zero().toHexString();
    const how = d.result === 'down' ? 'a clean down' : d.result === 'surrender' ? 'a yield' : d.result === 'boundary' ? 'the ring boundary' : d.result === 'timeout' ? 'the clock (draw)' : d.result;
    if (d.result === 'timeout') return `⚔ Duel vs ${other}: a draw on the clock`;
    if (d.result === 'cancel' || d.result === 'declined' || d.result === 'expired' || d.result === 'flag' || d.result === 'offline') return `⚔ Duel vs ${other} ended: ${d.result}`;
    return `⚔ Duel vs ${other}: ${won ? 'you WON' : lost ? 'you lost' : 'over'} — ${how}`;
  }

  /** Toasts + sounds on session transitions (once each). */
  private watchTransitions(duel: DuelRow | null, matchState: string): void {
    const key = duel ? `${duel.duelId}:${duel.state}` : '';
    if (duel && (key !== `${this.lastDuelId}:${this.lastDuelState}`)) {
      if (this.lastDuelId === duel.duelId || this.lastDuelId === 0n) {
        if (duel.state === 'active') { sfx('sfx_impact_energy', { gain: 0.5, cooldownMs: 100 }); bus.emit('atlas:status', { text: 'The duel is on.' }); }
        if (duel.state === 'finished' && atlas.identityHex !== null) bus.emit('atlas:status', { text: this.duelResultLine(duel, atlas.identityHex) });
      }
      this.lastDuelId = duel.duelId; this.lastDuelState = duel.state;
    }
    if (matchState !== this.lastMatchState) {
      if (matchState === 'round') { sfx('sfx_impact_energy', { gain: 0.5, cooldownMs: 100 }); bus.emit('atlas:status', { text: 'The round is live.' }); }
      if (matchState === 'staging' && this.lastMatchState === '') bus.emit('atlas:status', { text: 'Match found — go to your staging pad.' });
      this.lastMatchState = matchState;
    }
  }

  // ── Commands ─────────────────────────────────────────────────────────────
  private async command(cmd: string, args: string[]): Promise<string> {
    const conn = atlas.conn;
    if (!conn) return 'not connected';
    const say = (text: string): string => { bus.emit('atlas:status', { text }); return text; };
    const r = conn.reducers as unknown as Record<string, (a: Record<string, unknown>) => Promise<void>>;
    const call = async (name: string, a: Record<string, unknown>, okText: string): Promise<string> => {
      try { await r[name](a); return say(okText); } catch (e) { return say(`${String((e as Error).message ?? e).slice(0, 140)}`); }
    };
    switch (cmd.toLowerCase()) {
      case 'duel': {
        let hex: string | null = null;
        if (args[0]) hex = identityByName(args[0]);
        if (!hex) {
          // The nearest other traveler within 30 m.
          let bd = 30;
          for (const rem of atlas.remotes.values()) { const d = Math.hypot(rem.targetX - atlas.localX, rem.targetZ - atlas.localZ); if (d < bd) { bd = d; hex = rem.identityHex; } }
        }
        if (!hex) return say(args[0] ? `No traveler called ${args[0]}` : 'No traveler near enough to challenge');
        return call('duelInvite', { target: Identity.fromString(hex) }, `Challenge sent to ${playerName(hex)}.`);
      }
      case 'accept': case 'decline': {
        const inv = pendingInvite();
        if (!inv) return say('No challenge waiting');
        return call('duelRespond', { duelId: inv.duelId, accept: cmd === 'accept' }, cmd === 'accept' ? 'Accepted — walk to the ring.' : 'Declined.');
      }
      case 'ready': {
        const d = myDuel();
        if (d && d.state === 'staging') return call('duelReady', {}, 'Ready.');
        if (myMember()) return call('arenaReady', {}, 'Ready.');
        return say('Nothing to ready for');
      }
      case 'cancel': return call('duelCancel', {}, 'Duel withdrawn.');
      case 'yield': return call('duelSurrender', {}, 'You yield.');
      case 'arena': {
        const n = Math.max(1, Math.min(4, Number(args[0] ?? 4) || 4));
        return call('arenaQueueJoin', { teamSize: n }, `Queued for Relay Skirmish ${n}v${n}.`);
      }
      case 'leavequeue': return call('arenaQueueLeave', {}, 'Left the queue.');
      case 'leave': {
        if (myMember()) return call('arenaLeave', {}, 'You left the match.');
        if (myQueue()) return call('arenaQueueLeave', {}, 'Left the queue.');
        return say('Nothing to leave');
      }
      case 'enroll': {
        const s = nearestSector(atlas.localX, atlas.localZ);
        if (!s) return say('No conflict sector open');
        const side = args[0] ?? s.sideA;
        return call('fieldEnroll', { sectorId: s.sectorId, side }, `Enrolling on side ${side} at ${s.name} — 5 s.`);
      }
      case 'disengage': return call('fieldLeave', {}, 'Leaving the conflict.');
      case 'pvp': {
        const s = this.snapshot();
        return say(`pvp: ${s.context ?? 'no context'} · legal targets ${s.legal.length} · record D ${s.record?.duelsWon ?? 0}-${s.record?.duelsLost ?? 0}-${s.record?.duelsDrawn ?? 0} M ${s.record?.matchesWon ?? 0}-${s.record?.matchesLost ?? 0}`);
      }
      default: return say(`pvp: unknown command ${cmd}`);
    }
  }

  private snapshot() {
    const d = myDuel(), mem = myMember(), m = mem ? matchOf(mem.matchId) : null, e = myEnrollment();
    return {
      context: myContext(),
      duel: d ? { id: d.duelId.toString(), state: d.state, result: d.result, ring: [d.ringX, d.ringZ, d.ringR], readyA: d.readyA, readyB: d.readyB, gen: d.gen } : null,
      invite: pendingInvite() ? pendingInvite()!.duelId.toString() : null,
      match: m ? { id: m.matchId.toString(), state: m.state, round: m.round, winsA: m.winsA, winsB: m.winsB, capA: m.capA, capB: m.capB, team: mem?.team, alive: mem?.alive, ready: mem?.ready } : null,
      queue: myQueue() ? { teamSize: myQueue()!.teamSize } : null,
      enrollment: e ? { sectorId: e.sectorId.toString(), side: e.side, state: e.state } : null,
      sectors: rows<{ sectorId: bigint; name: string; active: boolean }>('pvpSector').filter((s) => s.active).map((s) => ({ id: s.sectorId.toString(), name: s.name })),
      actor: (() => { const a = myActor(); return a ? { life: a.life, pending: a.pendingActionId.toString(), attackReadyInMs: usOf(a.attackReadyAt) - nowMs(), equipReadyInMs: usOf(a.equipReadyAt) - nowMs(), protectedMs: usOf(a.protectedUntil) - nowMs() } : null; })(),
      record: myRecord(),
      legal: [...legalPlayerTargets()],
      tell: (() => { const t = tellAtMe(); return t ? { from: t.attacker.toHexString().slice(0, 8), item: t.itemKey, inMs: usOf(t.impactAt) - nowMs() } : null; })(),
      lastFeedback: this.lastFeedback,
      results: rows<ResultRow>('combatResult').slice(-8).map((x) => ({ id: x.resultId.toString(), outcome: x.outcome, dmg: x.dmg, item: x.itemKey, ctx: x.contextKind, kind: x.targetKind })),
    };
  }
}
